import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../components/ui/radio-group";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui-comp/textarea";
import { createAssetGroup, updateAssetGroup } from "../../../../helpers/api";
import { useGetAssetGroups } from "../../../../helpers/queries/scans/useGetInventory";

const CreateAssetGroupModal = ({ selectedIds, setSelectedIds }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("existing");
  const { data: assetGroupList } = useGetAssetGroups(0);

  const formSchema = useMemo(() => {
    if (type === "existing") {
      return z.object({
        name: z.string().optional(),
        asset_type: z.string().optional(),
        description: z.string().optional(),

        asset_group: z.string().min(1, "Please select a group"),
      });
    }

    return z.object({
      name: z.string().min(1, "Name cannot be empty"),
      asset_type: z.string().min(1, "Please select a type"),
      description: z.string().optional(),

      asset_group: z.string().optional(),
    });
  }, [type]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      asset_type: "",
      description: "",
    },
  });

  const { register } = form;

  const queryClient = useQueryClient();

  const { mutate: createGroup, isPending: pending } = useMutation({
    mutationFn: createAssetGroup,
    onSuccess: (data) => {
      toast(data?.message);
      queryClient.invalidateQueries({
        queryKey: ["inventory"],
      });
      setSelectedIds([]);

      queryClient.invalidateQueries({ queryKey: ["asset_groups"] });
      setOpen(false);
    },
    onError: (error) => {
      const errMsg =
        error?.response?.data?.message ||
        error.message ||
        "Something went wrong";

      toast.error(errMsg);
    },
  });

  const { mutate: updateGroup, isPending: pendingUpdate } = useMutation({
    mutationFn: updateAssetGroup,
    onSuccess: (data) => {
      toast(data?.message);
      queryClient.invalidateQueries({
        queryKey: ["inventory"],
      });
      setSelectedIds([]);

      queryClient.invalidateQueries({ queryKey: ["asset_groups"] });
      setOpen(false);
    },
    onError: (error) => {
      const errMsg =
        error?.response?.data?.message ||
        error.message ||
        "Something went wrong";

      toast.error(errMsg);
    },
  });

  const onSubmit = (data) => {
    if (type === "existing") {
      updateGroup({
        id: selectedIds,
        asset_group_id: Number(data.asset_group),
      });
    } else {
      createGroup({
        id: selectedIds, // should be array if multiple
        ...data,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="text-sm cursor-pointer text-blue-500 hover:text-blue-700 font-medium"
          variant="ghost"
          size="ghost"
          onClick={() => setOpen(true)}
        >
          <span className="font-medium pr-2">Add to group</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="">
        <DialogHeader className="text-left">
          <DialogTitle className="text-md text-semibold text-black">
            Add to group
          </DialogTitle>
          <DialogDescription className="hidden">
            Provide the website or IP address you'd like to monitor
          </DialogDescription>
        </DialogHeader>
        <hr />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-1"
          >
            <div className="flex flex-col">
              <label
                htmlFor="inputType"
                className="text-sm font-medium text-neutral1000 mb-3"
              >
                Group Type
              </label>
              <RadioGroup
                value={type}
                className="flex items-center gap-12 mb-2"
                onValueChange={(value) => setType(value)}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="existing" id="r1" />
                  <Label htmlFor="r1" className="m-0">
                    Existing group
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="new" id="r2" />
                  <Label htmlFor="r2" className="m-0">
                    New group
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {type === "existing" && (
              <FormField
                control={form.control}
                name="asset_group"
                render={({ field }) => (
                  <FormItem className="mb-4">
                    <FormLabel>Asset Group</FormLabel>
                    <Select
                      onValueChange={
                        field.onChange // Update the form field value
                      }
                      value={field?.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select asset group" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {assetGroupList?.data?.map((item) => (
                          <SelectItem value={String(item.id)} key={item.id}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {type === "new" && (
              <>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter name" {...field} />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="asset_type"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Asset Type</FormLabel>
                      <Select
                        onValueChange={
                          field.onChange // Update the form field value
                        }
                        value={field?.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select asset type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="web">Web Asset</SelectItem>
                          <SelectItem value="infrastructure">
                            Infrastructure Asset
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col">
                  <label
                    htmlFor="description"
                    className="text-sm  font-medium text-neutral1000"
                  >
                    Description
                  </label>
                  <Textarea
                    {...register("description")}
                    placeholder="Add asset description"
                  ></Textarea>
                </div>
              </>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                className="px-4 py-2 font-medium text-white"
                loading={type === "existing" ? pendingUpdate : pending}
              >
                Add
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAssetGroupModal;
